import 'maplibre-gl/dist/maplibre-gl.css';
import Map, { type ViewStateChangeEvent } from 'react-map-gl/maplibre';
import { Canvas } from 'react-three-map/maplibre';
import { Coordinates } from 'react-three-map';
import { Gltf } from '@react-three/drei';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clientRoutes } from '@/ApiPaths';
import { Billboard, Cylinder, Text } from '@react-three/drei';
import { Euler, ThreeEvent, Vector3 } from '@react-three/fiber';
import { Box, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

// TODO modularize, create page that links to this component and if possible delete mapbox

function calculatePinSize(zoom: number) {
  const zoomMin = 6;
  const zoomMax = 18;
  const scaleMin = 2000;
  const scaleMax = 1;

  const factor = Math.log(scaleMin / scaleMax) / (zoomMin - zoomMax);
  const scale = scaleMax * Math.exp(factor * (zoom - zoomMax));

  return scale;
}

/** Quantize zoom so pin scale does not React-update on every MapLibre frame. */
function quantizeZoom(zoom: number): number {
  return Math.round(zoom * 10) / 10;
}

// <Cylinder scale={[1000, 1000, 1000]} args={[1, 1, 50]} position={[0, 0, 0]}>
function LocationPin({
  latitude,
  longitude,
  gltfSource,
  rotation,
  position,
  pinName,
  pincolor,
  pinscale,
  onClick,
}: {
  latitude: number;
  longitude: number;
  gltfSource?: string;
  rotation?: Euler;
  position: Vector3;
  pinName: string;
  pincolor: string;
  pinscale: number;
  onClick: (e: ThreeEvent<MouseEvent>) => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <Coordinates latitude={latitude} longitude={longitude}>
      <ambientLight intensity={4} />

      <Billboard
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
        scale={[pinscale / 2, pinscale / 2, pinscale / 2]}
        position={[10, pinscale * 60, 10]}
        onClick={onClick}
      >
        <Text fontSize={10} color={hovered ? 'blue' : pincolor}>
          {pinName}
        </Text>
      </Billboard>

      <Cylinder
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
        scale={[pinscale, pinscale, pinscale]}
        args={[1, 1, 40]}
        position={[10, (pinscale / 2) * 50, 10]}
        onClick={onClick}
      >
        <meshPhongMaterial color={hovered ? 'blue' : pincolor} transparent opacity={0.8} />
      </Cylinder>
      {gltfSource && (
        <Suspense fallback={null}>
          <Gltf
            onPointerOver={() => setHovered(true)}
            onPointerOut={() => setHovered(false)}
            position={position}
            rotation={rotation}
            onClick={onClick}
            src={gltfSource}
          />
        </Suspense>
      )}
    </Coordinates>
  );
}

const mapStyles: Record<string, React.CSSProperties> = {
  searchContainer: {
    position: 'absolute',
    top: '20px',
    left: '10%',
    right: '10%',
    zIndex: 1,
    width: '80%',
  },
};

// value={message} onChange={(e) => setMessage(e.target.value)}
export function PortfolioMap() {
  const { t } = useTranslation();
  const [zoomLevel, setZoomLevel] = useState(6.8);
  const zoomRef = useRef(6.8);
  const pendingZoomRef = useRef(6.8);
  const zoomRafRef = useRef(0);
  const pinScale = useMemo(() => calculatePinSize(zoomLevel), [zoomLevel]);
  const [searchValue, setSearchValue] = useState('');

  const commitZoom = useCallback((zoom: number) => {
    const next = quantizeZoom(zoom);
    if (next === zoomRef.current) return;
    zoomRef.current = next;
    setZoomLevel(next);
  }, []);

  const scheduleZoomCommit = useCallback(
    (zoom: number) => {
      pendingZoomRef.current = zoom;
      if (zoomRafRef.current) return;
      zoomRafRef.current = window.requestAnimationFrame(() => {
        zoomRafRef.current = 0;
        commitZoom(pendingZoomRef.current);
      });
    },
    [commitZoom]
  );

  useEffect(
    () => () => {
      if (zoomRafRef.current) window.cancelAnimationFrame(zoomRafRef.current);
    },
    []
  );

  const handleZoom = useCallback(
    (event: ViewStateChangeEvent) => {
      scheduleZoomCommit(event.viewState.zoom);
    },
    [scheduleZoomCommit]
  );

  const handleZoomEnd = useCallback(
    (event: ViewStateChangeEvent) => {
      if (zoomRafRef.current) {
        window.cancelAnimationFrame(zoomRafRef.current);
        zoomRafRef.current = 0;
      }
      commitZoom(event.viewState.zoom);
    },
    [commitZoom]
  );

  return (
    <div style={{ height: '100vh', width: '100%', position: 'relative' }}>
      <Box style={mapStyles.searchContainer}>
        <TextInput
          placeholder={t('portfolio.search')}
          value={searchValue}
          onChange={(event) => setSearchValue(event.currentTarget.value)}
          leftSection={<IconSearch size={18} style={{ color: '#868e96' }} />}
          styles={{
            root: {
              width: '100%',
            },
            input: {
              height: '38px',
              paddingLeft: '36px',
              fontSize: '0.9rem',
              borderRadius: '4px',
              border: '1px solid #dee2e6',
              backgroundColor: 'white',
              boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
            },
          }}
        />
      </Box>
      <Map
        onZoom={handleZoom}
        onZoomEnd={handleZoomEnd}
        initialViewState={{ latitude: 49.7472958, longitude: 10.7373459, zoom: 6.8, pitch: 30 }}
        mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
      >
        {/* demand: idle map must not keep MapLibre+Three painting at display refresh */}
        <Canvas latitude={49.7472958} longitude={10.7373459} frameloop="demand">
          <LocationPin
            latitude={49.0549721}
            longitude={10.2272182}
            gltfSource={'/EcosphereExperienceCenter.glb'}
            pinName="Ecosphere Automation"
            position={[39, 0, 26]}
            pincolor="#48ff00"
            rotation={[0, 2 * Math.PI * (0 / 360), 0]}
            pinscale={pinScale}
            onClick={() => {
              if (window.confirm('Das Experience Center von Ecosphere besuchen?'))
                window.location.href = clientRoutes.demoRoom;
            }}
          />
          <LocationPin
            latitude={49.4110304}
            longitude={11.0580643}
            position={[0, 0, 12]}
            gltfSource={'/Mateco_gross.glb'}
            pinName="Freyler Industriebau"
            rotation={[0, 2 * Math.PI * (290 / 360), 0]}
            pincolor="#cc2126"
            pinscale={pinScale}
            onClick={() => {
              if (window.confirm('Den neuen Standort der mateco GmbH besuchen?'))
                window.location.href =
                  'https://portfolio.robotop.de/product/4684d62a-f457-40d0-b4a0-234c9053aa59';
            }}
          />
          <LocationPin
            latitude={49.7045745}
            longitude={11.0604039}
            position={[40, 0, -10]}
            gltfSource={'/Fraunhofer.glb'}
            pinName="Fraunhofer IKTS"
            pincolor="#33B585"
            rotation={[0, 2 * Math.PI * (180 / 360), 0]}
            pinscale={pinScale}
            onClick={() => {
              if (window.confirm('Batteriefertigung der Zukunft mit Fraunhofer erleben?'))
                window.location.href =
                  'https://portfolio.robotop.de/product/ef263432-7edd-42c8-ac2d-1f752bdb16ed';
            }}
          />
          <LocationPin
            latitude={47.8652656}
            longitude={11.788053}
            position={[40, 0, -10]}
            pinName="Rhopoint Instruments"
            pincolor="#005CA7"
            pinscale={pinScale}
            onClick={() => {
              if (window.confirm('Rhopointinstruments besuchen?'))
                window.location.href =
                  'https://portfolio.robotop.de/user/christian.dietz@rhopointinstruments.de';
            }}
          />
        </Canvas>
      </Map>
    </div>
  );
}
